import { paths } from '@type/api/apiSchema';
import type { HttpMethod, RequiredKeysOf } from 'openapi-typescript-helpers';
import { ApiPathParams, ApiQueryParams, ApiRequestBody, ApiOkResponse, ApiErrorResponse, ApiPathMethods } from '@type/api/apiUtilityFn';

import { baseURL, isFullUrl } from '@utils/data-fetching/utils';
import { buildSearchParams } from '@utils/data-fetching/searchParams';
import { buildApiPath } from '@utils/data-fetching/pathParams';
import { getServerReqHeaders } from '@utils/data-fetching/getServerReqHeaders';
import { parseApiErrorResponse, specialErrorCodesApiFetch } from '@lib/apiFetch/middlewares/specialErrorCodes';
import { logger } from '@utils/logger/logger';

/** Helpers */
type GenericObject = { [key: string | number]: unknown };
type ExtendedProp<T = undefined> = T extends object ? T & GenericObject : GenericObject;
type IsPropRequired<T> = T extends undefined ? false : true;

/** Response body type */
type BodyType<T = unknown> = {
  json: T;
  text: Awaited<ReturnType<Response['text']>>;
  blob: Awaited<ReturnType<Response['blob']>>;
  arrayBuffer: Awaited<ReturnType<Response['arrayBuffer']>>;
  stream: Response['body'];
};

/** The types of bodies that can be parsed */
type ParseAs = keyof BodyType;

/** The type of the parsed response */
type ParseAsResponse<T, Options> = Options extends { parseAs: ParseAs } ? BodyType<T>[Options['parseAs']] : T;

/** The response object for apiFetch function */
interface ApiFetchResponse<DataType> {
  data: DataType | undefined;
  error: ApiErrorResponse | undefined;
  response: Response | undefined;
}

/** Helper type for ApiFetchResponse assertion */
type AssertResponseType<Path extends keyof paths, Method extends ApiPathMethods<Path>, Options extends ApiFetchConfig<Path, Method>> = ApiFetchResponse<
  ParseAsResponse<ApiOkResponse<Path, Method>, Options>
>;

/** The apiFetch request options. Used internally by the apiFetch function */
interface ApiFetchOptions {
  method: HttpMethod;
  credentials: RequestCredentials | undefined;
  headers: HeadersInit | undefined;
  body?: BodyInit;
  signal?: AbortSignal;
}

/** The apiFetch request config. Used for user config. */
interface ApiFetchBaseConfig<Path extends keyof paths, Method> {
  method: Method;
  locale: Locale;
  headers?: Record<string, string>;
  parseAs?: ParseAs;
  // Allow for additional query params, ie for tracking
  queryParams?: ExtendedProp<ApiQueryParams<Path, ApiPathMethods<Path>>>;
  stringifyPayload?: boolean;
  cache?: RequestCache;
  next?: NextFetchRequestConfig;
  isClient?: boolean;
  signal?: AbortSignal;
  shouldFetch?: boolean;
  onSuccess?: (data?: ApiOkResponse<Path, ApiPathMethods<Path>>) => void;
  onError?: (error?: ApiErrorResponse) => void;
}

/** Make the pathParams & payload Required or Optional depending on the API endpoint schema */
type ApiFetchConfig<Path extends keyof paths, Method extends ApiPathMethods<Path>> = (IsPropRequired<ApiPathParams<Path, Method>> extends false
  ? ApiFetchBaseConfig<Path, Method> & { pathParams?: never }
  : ApiFetchBaseConfig<Path, Method> & { pathParams: ApiPathParams<Path, ApiPathMethods<Path>> }) &
  (IsPropRequired<RequiredKeysOf<ApiRequestBody<Path, Method>>> extends false ? { payload?: never } : { payload: ApiRequestBody<Path, ApiPathMethods<Path>> });

/** The function signature for the apiFetch function */
type ApiFetchFunc = <Path extends keyof paths, Method extends ApiPathMethods<Path>, Options extends ApiFetchConfig<Path, Method>>(
  apiEndpoint: Path,
  config: Options & { method: Method },
) => Promise<ApiFetchResponse<ParseAsResponse<ApiOkResponse<Path, Method>, Options>>>;

/**
 * @description Fetch function for the bestjobs API endpoints. The response, url, config options are typed
 * based on the OpenAPI schema. If you want to use for client-side requests, or don't want to include cookies, don't forget to set `isClient` to true.
 * To be used only in the `/app` directory.
 *
 * **Config Options:**
 * - `method`: One of the HTTP methods available for the endpoint. Required.
 * - `locale`: The locale to use for the request. Required.
 * - `headers`: Additional headers to include in the request. Optional.
 * - `parseAs`: The type of response body to parse. Can be one of: 'json', 'text', 'blob', 'arrayBuffer', 'stream'. Default: 'json'. Optional.
 * - `pathParams`: The path parameters for the endpoint. Required if the endpoint has path parameters, otherwise ignored.
 * - `queryParams`: The query parameters for the endpoint. Optional. Allows for additional query params (not only from Schema), ie for tracking.
 * - `payload`: The request body for the endpoint. Typed based on the OpenAPI schema. Required if the endpoint has payload, otherwise ignored.
 * - `stringifyPayload`: Whether to stringify the payload. Default: true. Use false for FormData or other formats that don't require the data to be a string. Optional.
 * - `cache`: The cache mode for the request. Optional.
 * - `next`: The Next.js fetch request config. Optional.
 * - `isClient`: Whether the request is being made on the client, or don't want to include cookies and server headers. Default: false. Optional. It is used to automatically add server request headers.
 * - `signal`: The AbortSignal to use for the request. Optional. Pass your own 'AbortController.signal' then call 'AbortController.abort()' to abort the request.
 * - `shouldFetch`: Whether to fetch the data. Default: true. Optional. Useful for conditional fetching.
 * - `onSuccess`: Callback function to be called on success. Optional.
 * - `onError`: Callback function to be called on error. Optional.
 *
 * **Return object:**
 * - `data`: The parsed response data. Undefined if the response is not ok.
 * - `error`: The parsed response error. Undefined if the response is ok.
 * - `response`: The raw response object. Undefined if the request was aborted.
 *
 * **Note:** Path parameters values must be strings as of version 7.0.0 of path-to-regexp,
 * don't forget to convert numbers to strings.
 *
 * @example
 * // Normal usage
 * const { data, error, response } = await apiFetch('/v1/some-endpoint/{orderId}', {
 *   // required; typed
 *   method: 'post',
 *   // required
 *   locale,
 *   // required if the endpoint has path parameters; typed
 *   pathParams: { orderId: '123' },
 *   // required if the endpoint has a request body schema; typed
 *   payload: { email: 'jon.snow@test.com' },
 *   // optional; typed; allows for additional query params outside schema
 *   queryParams: { tracking: '123', limit: 10 },
 *   // optional; default: 'json'; typed
 *   parseAs: 'blob',
 * });
 *
 * // use the raw response to check for ok, 204 status, read headers or other response properties
 * if (response?.ok), if (response?.status === 204), if (response?.headers.get('content-type')) ...
 *
 * // how to attach an AbortSignal to the request
 * const controller = new AbortController();
 * const { data, error, response } = await apiFetch('/v1/some-endpoint', { method: 'get', locale, signal: controller.signal });
 * controller.abort(); // call to abort the request
 */
export const apiFetch: ApiFetchFunc = async (apiEndpoint, config) => {
  // Type helpers
  type ResponseType = AssertResponseType<typeof apiEndpoint, typeof config.method, typeof config>;

  // Path helpers
  const apiPath = config.pathParams ? buildApiPath(apiEndpoint, config.pathParams) : apiEndpoint;
  const queryParams = config.queryParams ? buildSearchParams(config.queryParams) : '';
  const fetchUrl = `${isFullUrl(apiPath) ? '' : baseURL}${apiPath}${queryParams}`;

  // Create the fetch options
  const options: ApiFetchOptions = {
    method: config.method,
    credentials: 'include',
    headers: {
      'Accept-Language': config.locale,
      ...(!config?.isClient && (await getServerReqHeaders())),
      ...(config?.headers ?? {}),
    },
    ...(config?.cache && { cache: config.cache }),
    ...(config?.next && { next: config.next }),
    // allow the option of not stringify the payload, useful for FormData
    ...(config?.payload && {
      body: (config?.stringifyPayload ?? true) ? JSON.stringify(config.payload) : (config.payload as BodyInit),
    }),
    ...(config?.signal && { signal: config.signal }),
  };

  // If we don't want to fetch the data, return early
  if (config?.shouldFetch === false) {
    return Promise.resolve({
      data: undefined,
      error: undefined,
      response: undefined,
    });
  }

  // Fetch the data; assert the response type
  const response = await fetch(fetchUrl, options);
  // Safely clone the response for further use
  const clonedResponse = response.clone();

  // Add middleware functions here.
  // Pass the response to the middleware function.
  if (typeof specialErrorCodesApiFetch === 'function') {
    void specialErrorCodesApiFetch(clonedResponse);
  }

  // Return the ok response
  if (response.ok) {
    // if "stream", skip parsing entirely
    if (config?.parseAs === 'stream') {
      return {
        data: response.body,
        error: undefined,
        response: clonedResponse,
      } as ResponseType;
    }

    // Parse the response based on the parseAs option
    const data = (await response[config?.parseAs ?? 'json']()) as ResponseType['data'];

    // Handle onSuccess callback
    if (config?.onSuccess) config.onSuccess(data as never);

    // return parsed data
    return {
      data,
      error: undefined,
      response: clonedResponse,
    } as ResponseType;
  }

  // Parse the error response
  const error = await parseApiErrorResponse(response);

  try {
    const severity = response.status >= 500 ? 'error' : 'warn';
    logger[severity](
      {
        httpStatus: response.status,
        method: config.method,
        path: apiEndpoint,
        url: fetchUrl.split('?')[0],
        errorCodes: error?.errors?.map((e) => e?.code),
        errorMessages: error?.errors?.map((e) => e?.message?.slice(0, 200)),
      },
      `[apiFetch] ${response.status} ${config.method} ${apiEndpoint}`,
    );
  } catch {
    // logging must never break the error response flow
  }

  // Handle onError callback
  if (config?.onError) config.onError(error);

  // Return errors
  return {
    data: undefined,
    error,
    response: clonedResponse,
  };
};

/** Utility type to extract the resolved value from a promise */
type ApiFetchResponseArray<T extends ReturnType<ApiFetchFunc>> = {
  [K in keyof T]: T[K] extends Promise<infer R> ? R : never;
};

/** Function signature for the apiFetchAll function */
type ApiFetchAllFunc = <T extends ReturnType<ApiFetchFunc>[]>(apiFetches: T) => Promise<ApiFetchResponseArray<T>>;

/**
 * @description Helper to make sure we're fetching all the data concurrently using Promise.all.
 * @param apiFetches The array of apiFetch promises to resolve.
 * @returns An array of objects with the data, error and response.
 *
 * @example
 * const firstPromise = apiFetch('/v1/company/billing-data', { method: 'get', locale: 'en' });
 * const secondPromise = apiFetch('/v1/contact-info', { method: 'get', locale: 'en', shouldFetch: someCondition });
 *
 * const [{ data: first }, { data: second }] = await apiFetchAll([firstPromise, secondPromise]);
 */
export const apiFetchAll: ApiFetchAllFunc = async (apiFetches) => {
  // Concurrently fetch all the data
  const results = await Promise.all(apiFetches);

  // Map over the results and destructure each response with correct typing
  return results.map((result) => {
    const { data, error, response } = result as ApiFetchResponse<ApiFetchResponseArray<typeof apiFetches>[number]>;
    return { data, error, response };
  }) as ApiFetchResponseArray<typeof apiFetches>;
};

/** Utility type to extract the resolved value from a promise */
type ApiFetchSettledResponseArray<T extends ReturnType<ApiFetchFunc>> = {
  [K in keyof T]: T[K] extends Promise<infer R> ? R : never;
};

/** Function signature for the apiFetchAllSettled function */
type ApiFetchAllSettledFunc = <T extends ReturnType<ApiFetchFunc>[]>(apiFetches: T) => Promise<ApiFetchSettledResponseArray<T>>;

/**
 * @description Helper to make sure we're fetching all the data concurrently using Promise.allSettled.
 * @param apiFetches The array of apiFetch promises to resolve.
 * @returns An array of objects with the data, error and response.
 *
 * @example
 * const firstPromise = apiFetch('/v1/company/billing-data', { method: 'get', locale: 'en' });
 * const secondPromise = apiFetch('/v1/contact-info', { method: 'get', locale: 'en', shouldFetch: someCondition });
 *
 * const [{ data: first }, { data: second }] = await apiFetchAllSettled([firstPromise, secondPromise]);
 */
export const apiFetchAllSettled: ApiFetchAllSettledFunc = async (apiFetches) => {
  // Concurrently fetch all the data
  const results = await Promise.allSettled(apiFetches);

  // Map over the results and destructure each response with correct typing
  return results.map((result) => {
    if (result.status === 'fulfilled') {
      const { data, error, response } = result.value as ApiFetchResponse<ApiFetchResponseArray<typeof apiFetches>[number]>;
      return { data, error, response };
    }
    return { data: undefined, error: result.reason as ApiErrorResponse, response: undefined };
  }) as ApiFetchSettledResponseArray<typeof apiFetches>;
};
